/**
 * Listing Filter Alpine Component
 *
 * Handles AJAX filtering and pagination for listing blocks.
 * Integrates with the global loader and provides smooth scrolling.
 *
 * Usage:
 * <div x-data="listingFilter({
 *     url: 'current-page-url',
 *     contentId: 'listing-container-id',
 *     loader: true,
 *     staticFilters: { blockId: 123 },
 *     urlFilters: { brand: '', application: '', features: '' }
 * })">
 */
export default (config = {}) => ({
    url: config.url || '',
    contentId: config.contentId || '',
    totalResults: config.totalResults || 0,
    offset: config.offset || 160,
    loader: config.loader || false,
    staticFilters: config.staticFilters || {}, // Never appear in URL
    urlFilters: config.urlFilters || {}, // Appear in URL and history

    init() {
        this.totalResults = document.querySelector("#" + this.contentId).querySelector(".card-wrapper").dataset.total;
    },

    loadTableData(url = "", refresh = false) {
        let _this = this;

        // Prevent multiple simultaneous requests
        if (this.$store.globalLoader.isLoading) {
            return false;
        }

        // Show global loader if enabled
        if (_this.loader) {
            this.$store.globalLoader.show();
        }

        // Build URL with filters
        if (url == "") {
            url = _this.createFreshURL();
        } else {
            // Append current filters to the provided URL
            url = _this.appendFiltersToUrl(url);
        }
        // Update browser history (only with URL filters, not static filters)
        let urlForHistory = _this.createURLForHistory(url);
        // if (urlForHistory.includes('?')) {
            if (!refresh) {
                window.history.pushState({}, "", urlForHistory);
            }
        // }

        // Make AJAX request
        fetch(url, {
            method: "GET",
            cache: "no-cache",
            redirect: "follow",
            credentials: "same-origin",
            headers: {
                "X-Requested-With": "XMLHttpRequest"
            }
        })
        .then((response) => response.text())
        .then((data) => {
            let selector = document.querySelector("#" + _this.contentId);
            if (selector) {
                selector.innerHTML = data;
                this.totalResults = selector.querySelector(".card-wrapper").dataset.total;

                // Smooth scroll to content with offset
                let elementTop = selector.parentElement.getBoundingClientRect().top + window.pageYOffset;
                if(selector.querySelector(".swiper-slider")) {
                    window.initializeSwipers(selector);
                }
                window.scroll({
                    behavior: "smooth",
                    top: elementTop - _this.offset
                });

                this.$store.globalLoader.hide();
            }
            this.$store.globalLoader.hide();
        })
        .catch((error) => {
            console.error('Listing filter error:', error);
            this.$store.globalLoader.hide();
        });
    },

    // Handle form submission for filters
    handleFilterForm(event) {
        event.preventDefault();

        // Get form data
        const formData = new FormData(event.target);
        // Update urlFilters with form values
        for (let [key, value] of formData.entries()) {
            if (this.urlFilters.hasOwnProperty(key)) {
                this.urlFilters[key] = value;
            }
        }

        // Trigger data load
        this.loadTableData();
    },

    // Clear all URL filters
    clearFilters() {
        Object.keys(this.urlFilters).forEach(key => {
            this.urlFilters[key] = '';
        });
        this.loadTableData();
    },

    removeParams(currentValue) {
        let _this = this;

        // Remove matching filter values from URL filters only
        (Object.keys(this.urlFilters)).forEach(function(value, key) {
            if (_this.urlFilters[value] == currentValue) {
                _this.urlFilters[value] = "";
            }
        });

        this.loadTableData();
    },

    createFreshURL() {
        let _this = this;
        let url = this.url + "?";

        // Add all non-empty static filters (for backend processing)
        (Object.keys(this.staticFilters)).forEach(function(key) {
            if (_this.staticFilters[key] != "") {
                url += "&" + key + "=" + _this.staticFilters[key];
            }
        });

        // Add all non-empty URL filters
        (Object.keys(this.urlFilters)).forEach(function(key) {
            if (_this.urlFilters[key] != "") {
                url += "&" + key + "=" + _this.urlFilters[key];
            }
        });

        return url;
    },

    createURLForHistory(url) {
        let _this = this;
        let urlObj = new URL(url, window.location.origin);

        // Clear all existing parameters (including static filters)
        urlObj.search = '';

        // Only add URL filters to browser history
        (Object.keys(this.urlFilters)).forEach(function(key) {
            if (_this.urlFilters[key] != "") {
                urlObj.searchParams.set(key, _this.urlFilters[key]);
            }
        });

        return urlObj.toString();
    },

    appendFiltersToUrl(url) {
        let _this = this;
        let urlObj = new URL(url, window.location.origin);

        // Add static filters (for backend processing)
        (Object.keys(this.staticFilters)).forEach(function(key) {
            if (_this.staticFilters[key] != "") {
                urlObj.searchParams.set(key, _this.staticFilters[key]);
            }
        });

        // Add URL filters
        (Object.keys(this.urlFilters)).forEach(function(key) {
            if (_this.urlFilters[key] != "") {
                urlObj.searchParams.set(key, _this.urlFilters[key]);
            }
        });

        return urlObj.toString();
    }
});
